/**
 * Route table (§3.1). Paths are relative to the demo base, so `/demo/m/WOLF42/map` and
 * `/m/WOLF42/map` render the same screens.
 */
import { lazy, Suspense } from 'react';
import { LandingScreen } from '../features/landing/LandingScreen';
import { DemoShortcutRoute } from '../dev/demoRoutes';
import { findDemoShortcut } from '../dev/demoShortcuts';
import { Button } from '../ui/Button';
import { Page } from '../ui/Page';
import { PixelSpinner } from '../ui/PixelSpinner';
import { useBackend } from './backendContext';
import { matchPath, navigate, useLocation } from './router';
import { Redirect } from './Link';

// Everything but the landing page loads on demand, so a first visit stays light.
const CreateMatchScreen = lazy(() =>
  import('../features/create/CreateMatchScreen').then((m) => ({ default: m.CreateMatchScreen })),
);
const EnterCodeScreen = lazy(() =>
  import('../features/join/EnterCodeScreen').then((m) => ({ default: m.EnterCodeScreen })),
);
const JoinScreen = lazy(() => import('../features/join/JoinScreen').then((m) => ({ default: m.JoinScreen })));
const PendingScreen = lazy(() =>
  import('../features/pending/PendingScreen').then((m) => ({ default: m.PendingScreen })),
);
const AppSettingsScreen = lazy(() =>
  import('../features/settings/AppSettingsScreen').then((m) => ({ default: m.AppSettingsScreen })),
);
const ClaimScreen = lazy(() =>
  import('../features/transfer/ClaimScreen').then((m) => ({ default: m.ClaimScreen })),
);
const MatchGate = lazy(() => import('../features/match/MatchGate').then((m) => ({ default: m.MatchGate })));

const CODE_RE = /^[A-Za-z0-9]{4,12}$/;
const TOKEN_RE = /^[A-Za-z0-9_-]{20,128}$/;

function RouteLoading() {
  return (
    <div role="status" style={{ display: 'grid', placeItems: 'center', height: '100%' }}>
      <span style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}>
        <PixelSpinner /> Loading…
      </span>
    </div>
  );
}

export function AppRoutes() {
  return (
    <Suspense fallback={<RouteLoading />}>
      <RouteSwitch />
    </Suspense>
  );
}

function RouteSwitch() {
  const { path } = useLocation();
  const backend = useBackend();

  // Demo shortcuts (`/demo/join`, `/demo/map`…) win over real routes of the same name.
  if (backend.mode === 'demo') {
    const shortcut = findDemoShortcut(path);
    if (shortcut) return <DemoShortcutRoute shortcut={shortcut} />;
  }

  if (path === '/') return <LandingScreen />;
  if (path === '/new') return <CreateMatchScreen />;
  if (path === '/join') return <EnterCodeScreen />;
  if (path === '/settings') return <AppSettingsScreen />;

  const claim = matchPath('/claim/:token', path);
  if (claim?.token && TOKEN_RE.test(claim.token))
    return <ClaimScreen key={claim.token} token={claim.token} />;

  const joinShort = matchPath('/join/:code', path);
  if (joinShort?.code && CODE_RE.test(joinShort.code)) {
    return <Redirect to={`/m/${joinShort.code.toUpperCase()}/join`} />;
  }

  const match = /^\/m\/([^/]+)(?:\/(.*))?$/.exec(path);
  if (match) {
    const code = decodeURIComponent(match[1] ?? '').toUpperCase();
    const sub = match[2] ?? '';
    if (!CODE_RE.test(code)) return <NotFound />;
    if (sub === 'join') return <JoinScreen code={code} />;
    if (sub === 'pending') return <PendingScreen code={code} />;
    return <MatchGate key={code} code={code} sub={sub} />;
  }

  return <NotFound />;
}

function NotFound() {
  return (
    <Page title="Nothing here" backTo="/" backLabel="Start">
      <p>That link doesn't lead anywhere in Seven Dawns. Check it with whoever sent it.</p>
      <Button variant="primary" size="l" onClick={() => navigate('/')}>
        Back to start
      </Button>
    </Page>
  );
}
